import React, { Component } from 'react';

import cloneDeep from 'lodash/cloneDeep';

import Button from '@atlaskit/button';
import RefreshIcon from '@atlaskit/icon/glyph/refresh';
import WatchFilledIcon from '@atlaskit/icon/glyph/watch-filled';

import { QAlityFreeLabel } from '@qality/common-components/src/atoms/QAlityFreeLabel';
import {
  FEATURE_FLAG,
  UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE,
} from '@qality/common-config';
import { ReportFilterOptions } from '@qality/common-model';
import { TestTypeProvider } from '@qality/common-pages/src/TestTypesPage/TestTypesProvider';
import { StorageOfUniqueServices } from '@qality/common-service';

import DateRangeSelect from '../select/DateRangeSelect';
import MoreFiltersSelect, {
  SelectOptions as MoreFiltersSelectOptions,
} from '../select/MoreFiltersSelect';
import MoreFiltersTagList from '../select/MoreFiltersTagList';
import RecentAndMoreProjectSelect from '../select/RecentAndMoreProjectSelect';
import RequirementSelect from '../select/RequirementSelect';
import SearchSelect from '../select/SearchSelect';
import StatusSelect from '../select/StatusSelect';
import { TestCycleSelect } from '../select/TestCycleSelect';
import TestTypeReportFilter from '../select/TestTypeReportFilter';
import UserSelect from '../select/UserSelect';
import './_report-filters.scss';

type SelectProps = {
  isClearable: boolean;
  useAllOption: boolean;
};

type FilterObject = {
  name: string;
  value: string;
};

type Props = {
  projectSelect: SelectProps;
  userSelect: boolean;
  requirementSelect: boolean;
  searchSelect: boolean;
  dateRangeSelect: boolean;
  statusSelect: boolean;
  moreFiltersSelect: boolean;
  testCycleSelect: boolean;
  testTypeSelect: boolean;
  isDisabled: boolean;
  onGenerateReport: (filters: Array<FilterObject>) => void;
  mapFilters: (filters: Array<Array<string>>) => void;
  onLoad: (filters: Array<FilterObject>) => void;
  getDefaultDates: () => { startDate: string; endDate: string };
  isLoading: boolean;
};

type State = {
  filterOptions: ReportFilterOptions;
  filtersChanged: boolean;
  startDate: string;
  endDate: string;
  projectId: string;
  initialized: boolean;
  userId: string;
  testCyclesIds: string;
  testTypesIds: string;
};

// NOTE: 'value' has to match the query parameter name - important!
const MORE_FILTERS_OPTIONS: MoreFiltersSelectOptions = [
  {
    label: 'Show only active test cases',
    value: 'onlyActiveTestCases',
    isEnabled: false,
  },
];

const FIRST_PAGE = 0;

export default class ReportFilters extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      filterOptions: {},
      startDate: '',
      endDate: '',
      projectId: '',
      userId: '',
      testCyclesIds: '',
      testTypesIds: '',
      filtersChanged: false,
      initialized: false,
    };
  }

  static defaultProps = {
    projectSelect: null,
    userSelect: false,
    requirementSelect: false,
    searchSelect: false,
    testCycleSelect: false,
    testTypeSelect: false,
    dateRangeSelect: false,
  };

  async componentDidMount(): Promise<void> {
    await this.readUrlParams();

    this.props.onLoad(this.getFilters());
  }

  updateUrlArguments = (urlSearchParams: Array<FilterObject>): void => {
    StorageOfUniqueServices.get('URLService').setUrlParams(urlSearchParams);
  };

  // eslint-disable-next-line complexity
  readUrlParams = async (): Promise<void> => {
    const urlSearchParams = await StorageOfUniqueServices.get(
      'URLService'
    ).getURLSearchParams();
    const { startDate, endDate } = this.getDates(urlSearchParams);
    const requirementTypeName =
      urlSearchParams.get('requirementTypeName') ?? '';
    const search = urlSearchParams.get('search');
    const projectId = urlSearchParams.get('projectId');
    const userId = urlSearchParams.get('accountId');
    const statuses = urlSearchParams.getAll('statusesIds').join(',');
    const testCyclesIds = urlSearchParams.getAll('testCyclesIds').join(',');
    const testTypesIds = urlSearchParams.getAll('testTypesIds').join(',');

    const onlyActiveTestCases =
      urlSearchParams.get('onlyActiveTestCases') === 'true';
    const filterOptions = {
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(projectId && { projectId }),
      ...(userId && { accountId: userId }),
      ...(statuses && { statuses }),
      ...(testCyclesIds && { testCyclesIds }),
      ...(testTypesIds && { testTypesIds }),
      ...(onlyActiveTestCases && { onlyActiveTestCases }),
      ...(requirementTypeName && { requirementTypeName }),
      ...(search && { search }),
    };

    this.setState({
      startDate,
      endDate,
      requirementTypeName,
      search,
      projectId,
      userId,
      statuses,
      testCyclesIds,
      testTypesIds,
      onlyActiveTestCases,
      initialized: true,
      filtersChanged: false,
      filterOptions: filterOptions,
    });

    const filterOptionsArray: FilterObject[] = Object.entries(
      filterOptions
    ).map(([name, value]) => ({ name, value }));
    this.updateUrlArguments(filterOptionsArray);
  };

  getDates = (
    urlSearchParams: URLSearchParams
  ): { startDate: string; endDate: string } => {
    const startDateTemp = urlSearchParams.get('startDate');
    const endDateTemp = urlSearchParams.get('endDate');
    if (this.props.getDefaultDates && (!startDateTemp || !endDateTemp)) {
      const { startDate, endDate } = this.props.getDefaultDates();
      return { startDate, endDate };
    }
    if (startDateTemp === 'none' && endDateTemp === 'none') {
      return { startDate: null, endDate: null };
    }
    return { startDate: startDateTemp, endDate: endDateTemp };
  };

  handleChangeOfDate = (newFilterOptions): void => {
    if (
      newFilterOptions['startDate'] === 'Invalid date' ||
      newFilterOptions['endDate'] === 'Invalid date'
    ) {
      newFilterOptions['startDate'] = 'none';
      newFilterOptions['endDate'] = 'none';
    }
  };

  onChange = async (filterValues: Array<any>): Promise<void> => {
    const newFilterOptions = cloneDeep(this.state.filterOptions);
    filterValues.forEach((filterValue) => {
      if (filterValue.name === 'project') {
        newFilterOptions['projectId'] = filterValue.value;
        delete newFilterOptions['project'];
      } else {
        newFilterOptions[filterValue.name] = filterValue.value;
      }
      if (filterValue.name === 'startDate' || filterValue.name === 'endDate') {
        this.handleChangeOfDate(newFilterOptions);
      }
    });

    this.setState({
      filterOptions: newFilterOptions,
      filtersChanged: true,
    });
  };

  getFilters = () => {
    const tableOfFilterOptions = [];
    const filters = this.props.mapFilters(
      Object.entries(this.state.filterOptions)
    );

    Object.keys(filters).forEach((filterKey) => {
      tableOfFilterOptions.push({ name: filterKey, value: filters[filterKey] });
    });
    return tableOfFilterOptions;
  };

  generateReport = () => {
    const tableOfFilterOptions = this.getFilters();

    this.props.onGenerateReport([
      ...tableOfFilterOptions,
      { name: 'pageNumber', value: FIRST_PAGE },
    ]);

    this.setState({
      filtersChanged: false,
    });
  };

  render(): JSX.Element {
    return (
      <div className='report-filters-container'>
        <h5 className='report-filters-header'>Filters: </h5>
        {!FEATURE_FLAG.isPremium && (
          <QAlityFreeLabel
            text='Advanced filtering available in QAlity Plus.'
            linkName='Learn more'
            linkUrl=' https://soldevelo.atlassian.net/wiki/spaces/QS/pages/2444165210/QAlity+vs+QAlity+Plus'
          />
        )}
        {this.state.initialized && (
          <div className='report-filters-component-container'>
            <div className='flex-container'>
              <div className='filters-container'>
                {this.props.projectSelect && (
                  <RecentAndMoreProjectSelect
                    {...this.props.projectSelect}
                    projectId={this.state.projectId}
                    onChange={this.onChange}
                    isDisabled={this.props.isDisabled}
                    classNames='filter-common project-select-container'
                    tooltip={
                      this.props.isDisabled
                        ? UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE
                        : undefined
                    }
                  />
                )}
                {this.props.testCycleSelect && (
                  <TestCycleSelect
                    testCyclesIds={
                      this.state.testCyclesIds.length === 0
                        ? []
                        : this.state.testCyclesIds.split(',')
                    }
                    onChange={(testCycles) => {
                      this.onChange([
                        {
                          name: 'testCyclesIds',
                          value: testCycles.map((tc) => tc.id),
                        },
                      ]);
                    }}
                    isDisabled={this.props.isDisabled}
                    label='Test Cycle'
                    tooltip={
                      this.props.isDisabled &&
                      UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE
                    }
                  />
                )}
                {this.props.userSelect && (
                  <UserSelect
                    userId={this.state.userId}
                    onChange={this.onChange}
                    isDisabled={this.props.isDisabled}
                    tooltip={
                      this.props.isDisabled
                        ? UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE
                        : undefined
                    }
                  />
                )}
                {this.props.statusSelect && (
                  <StatusSelect
                    statuses={this.state.statuses}
                    onChange={this.onChange}
                    isDisabled={this.props.isDisabled}
                    tooltip={
                      this.props.isDisabled
                        ? UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE
                        : undefined
                    }
                  />
                )}
                {this.props.dateRangeSelect && (
                  <DateRangeSelect
                    onChange={this.onChange}
                    isDisabled={this.props.isDisabled}
                    tooltip={
                      this.props.isDisabled
                        ? UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE
                        : undefined
                    }
                    startDate={this.state.startDate}
                    endDate={this.state.endDate}
                  />
                )}
                {this.props.testTypeSelect &&
                  StorageOfUniqueServices.get('LicenseService').isAdvanced && (
                    <TestTypeProvider>
                      <TestTypeReportFilter
                        testTypesIds={this.state.testTypesIds}
                        setTestTypesids={(testTypesIds) => {
                          this.setState({ testTypesIds });
                        }}
                        onChange={this.onChange}
                      />
                    </TestTypeProvider>
                  )}
                {this.props.requirementSelect && (
                  <RequirementSelect
                    projectId={this.state.filterOptions.projectId}
                    requirementTypeName={this.state.requirementTypeName}
                    onChange={this.onChange}
                    isDisabled={this.props.isDisabled}
                    tooltip={
                      this.props.isDisabled
                        ? UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE
                        : undefined
                    }
                  />
                )}
                {this.props.searchSelect && (
                  <SearchSelect
                    requirementTypeName={
                      this.state.filterOptions.requirementTypeName
                    }
                    search={this.state.search}
                    onChange={this.onChange}
                    isDisabled={this.props.isDisabled}
                    tooltip={
                      this.props.isDisabled
                        ? UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE
                        : undefined
                    }
                  />
                )}
                <div className='button-container'>
                  {this.props.moreFiltersSelect && (
                    <MoreFiltersSelect
                      onChange={this.onChange}
                      filterOptions={MORE_FILTERS_OPTIONS.map((filter) => {
                        return {
                          ...filter,
                          isEnabled: this.state.filterOptions?.[filter.value],
                        };
                      })}
                      targetClassName='more-filters-button'
                    />
                  )}
                  <Button
                    onClick={this.generateReport}
                    isDisabled={this.props.isLoading}
                    appearance={
                      this.state.filtersChanged ? 'primary' : 'default'
                    }
                    iconBefore={
                      this.state.filtersChanged ? (
                        <WatchFilledIcon label='generate report' />
                      ) : (
                        <RefreshIcon label='refresh report' />
                      )
                    }
                    className='refresh-report-button'
                  >
                    {this.state.filtersChanged
                      ? 'Generate report'
                      : 'Refresh report'}
                  </Button>
                </div>
              </div>
            </div>
            <div className='flex-container'>
              {this.props.moreFiltersSelect && (
                <div className='tags-container'>
                  <MoreFiltersTagList
                    onTagRemove={this.onChange}
                    tags={MORE_FILTERS_OPTIONS.map((filter) => {
                      return {
                        filterLabel: filter.label,
                        filterName: filter.value,
                        isEnabled: this.state.filterOptions?.[filter.value],
                      };
                    })}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}
